import type { RichAircraft } from '../data/enrich';
import { extrapolate, extrapolationDtFor, project } from '../lib/geo';
import {
  ICON_DEFS,
  isEmergency,
  normalizeSvgSize,
  recolorSvg,
  AIRCRAFT_ICON_BASE,
  ICON_CANVAS_PX,
} from './aircraftIcons';

export { isEmergency, ICON_IMAGE_EXPR } from './aircraftIcons';

/** Label text for map symbols and 3D model tags. */
export function aircraftDisplayLabel(
  ac: Pick<RichAircraft, 'icao' | 'callsign' | 'registration'>,
  preferCallsign: boolean,
): string {
  if (preferCallsign) return ac.callsign ?? ac.registration ?? ac.icao;
  return ac.icao;
}

export interface AircraftFeatureProps {
  icao: string;
  callsign: string | null;
  cls: string;
  alt: number;
  track: number | null;
  speed: number | null;
  squawk: string | null;
  emergency: boolean;
  selected: boolean;
  reg: string | null;
  icon: string;
  iconImage: string;
  special: string | null;
}

export interface FlightPathFeatureProps {
  icao: string;
  cls: string;
  selected: boolean;
  special: string | null;
  emergency: boolean;
}

const PATH_SECONDS = 180;

/** Seconds of flight path drawn ahead of each aircraft. */
export const FLIGHT_PATH_LOOKAHEAD_SEC = PATH_SECONDS;

const FEET_TO_METERS = 0.3048;

/** Vertical exaggeration applied to real altitudes so height differences read clearly in a 3D side view. */
export const ALTITUDE_EXAGGERATION = 1.5;

/** Default half-span of the aircraft silhouette footprint, in meters (wingspan = 2× this). */
export const PILLAR_HALF_SPAN_PLANE_M = 500;

/** Helicopter hologram cylinder radius in meters (simple extruded disc). */
export const HELICOPTER_HOLOGRAM_RADIUS_M = 80;

/** Geographic half-span (meters) for an aircraft kind's altitude hologram footprint. */
export function pillarHalfSpanM(iconKind: string): number {
  if (iconKind === 'helicopter' || iconKind === 'military-helicopter') {
    return HELICOPTER_HOLOGRAM_RADIUS_M;
  }
  return PILLAR_HALF_SPAN_PLANE_M;
}

/**
 * Normalized top-down silhouettes in a local frame (x = right-wing, y = nose),
 * wound counterclockwise so they satisfy GeoJSON's right-hand rule for exterior
 * rings. Coordinates are scaled by `pillarHalfSpanM(iconKind)` at render time.
 */
const PLANE_SHAPE: readonly [number, number][] = [
  [0.0, 1.0], [-0.09, 0.45], [-0.3, 0.05], [-1.0, -0.12], [-0.3, -0.22],
  [-0.14, -0.55], [-0.32, -0.9], [-0.1, -0.7], [-0.05, -1.0],
  [0.05, -1.0], [0.1, -0.7], [0.32, -0.9], [0.14, -0.55],
  [0.3, -0.22], [1.0, -0.12], [0.3, 0.05], [0.09, 0.45], [0.0, 1.0],
];

const FIGHTER_SHAPE: readonly [number, number][] = [
  [0.0, 1.0], [-0.22, 0.3], [-1.0, -0.55], [-0.18, -0.45], [-0.1, -0.75],
  [-0.3, -1.0], [0.0, -0.85], [0.3, -1.0], [0.1, -0.75],
  [0.18, -0.45], [1.0, -0.55], [0.22, 0.3], [0.0, 1.0],
];

const HELI_CYLINDER_SEGMENTS = 24;

/** Unit-circle footprint extruded as a cylinder for helicopter altitude holograms. */
const HELICOPTER_CYLINDER_SHAPE: readonly [number, number][] = (() => {
  const pts: [number, number][] = [];
  for (let i = 0; i < HELI_CYLINDER_SEGMENTS; i++) {
    const t = (2 * Math.PI * i) / HELI_CYLINDER_SEGMENTS;
    pts.push([Math.cos(t), Math.sin(t)]);
  }
  pts.push(pts[0]);
  return pts;
})();

const BALLOON_SHAPE: readonly [number, number][] = [
  [0.0, 1.0], [-0.7, 0.7], [-1.0, 0.0], [-0.7, -0.7],
  [0.0, -1.0], [0.7, -0.7], [1.0, 0.0], [0.7, 0.7], [0.0, 1.0],
];

function silhouetteFor(iconKind: string): readonly [number, number][] {
  switch (iconKind) {
    case 'helicopter':
    case 'military-helicopter':
      return HELICOPTER_CYLINDER_SHAPE;
    case 'military-fighter':
      return FIGHTER_SHAPE;
    case 'balloon':
      return BALLOON_SHAPE;
    default:
      return PLANE_SHAPE;
  }
}

export interface AircraftAltitudeFeatureProps {
  icao: string;
  altM: number;
  emergency: boolean;
  selected: boolean;
  icon: string;
  special: string | null;
}

/** Shared aircraft color expression (by special kind / emergency / icon kind). */
export const AIRCRAFT_COLOR_EXPR: unknown[] = [
  'case',
  ['==', ['get', 'special'], 'presidential'], '#fde047',
  ['get', 'emergency'], '#ef4444',
  [
    'match', ['get', 'icon'],
    'military-fighter', '#fb923c',
    'military-transport', '#f97316',
    'military-other', '#fdba74',
    'military-helicopter', '#f97316',
    'helicopter', '#22d3ee',
    'balloon', '#f472b6',
    'heavy-commercial', '#38bdf8',
    'commercial', '#60a5fa',
    'regional', '#7dd3fc',
    'turboprop', '#67e8f9',
    'private-jet', '#c4b5fd',
    'light-prop', '#a7f3d0',
    'glider', '#bbf7d0',
    'government', '#fbbf24',
    '#cbd5e1',
  ],
];

/**
 * Extruded height (meters) for an aircraft altitude pillar. `altM` (already
 * exaggerated) is used up to zoom 9, then damped at higher zooms so pillars
 * stay a reasonable fraction of the viewport when zoomed in close.
 */
export const ALTITUDE_HEIGHT_EXPR: unknown[] = [
  'interpolate', ['linear'], ['zoom'],
  0, ['get', 'altM'],
  9, ['get', 'altM'],
  11, ['*', ['get', 'altM'], 0.6],
  13, ['*', ['get', 'altM'], 0.3],
  14, ['*', ['get', 'altM'], 0.15],
];

/** Thin extrusion slice (meters) for the black ground-shadow slab at the
 *  base of each pillar. A pure zoom interpolate (no data) so it's valid for
 *  `fill-extrusion-base/height`. The pillar is stacked on top of this slab. */
export const ALTITUDE_CAP_SLICE_EXPR: unknown[] = [
  'interpolate', ['linear'], ['zoom'],
  0, 300,
  6, 300,
  9, 150,
  11, 75,
  13, 50,
  14, 40,
];

/**
 * Build a GeoJSON FeatureCollection of aircraft points, extrapolating each
 * position by its own age (snapshot wall time + per-airframe `seen`).
 */
export function buildAircraftGeoJSON(
  list: RichAircraft[],
  snapshotTimeSec: number,
  nowSec: number,
  selectedIcao: string | null,
  selectedFetchedAtSec = 0,
): GeoJSON.FeatureCollection<GeoJSON.Point, AircraftFeatureProps> {
  const features: GeoJSON.Feature<GeoJSON.Point, AircraftFeatureProps>[] = [];
  for (const a of list) {
    const dtSec = extrapolationDtFor(a, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec);
    const pos = extrapolate(a, dtSec);
    if (!pos) continue;
    const emergency = isEmergency(a);
    const iconImage =
      a.specialKind === 'presidential' ? 'plane-presidential' : `plane-${a.iconKind}${emergency ? '-emergency' : ''}`;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
      properties: {
        icao: a.icao,
        callsign: a.callsign,
        cls: a.flightClass,
        alt: a.altBaro ?? 0,
        track: a.track,
        speed: a.groundSpeed,
        squawk: a.squawk,
        emergency,
        selected: a.icao === selectedIcao,
        reg: a.registration,
        icon: a.iconKind,
        iconImage,
        special: a.specialKind,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** Build short projected path lines from each aircraft's speed and track. */
export function buildFlightPathGeoJSON(
  list: RichAircraft[],
  snapshotTimeSec: number,
  nowSec: number,
  selectedIcao: string | null,
  selectedFetchedAtSec = 0,
): GeoJSON.FeatureCollection<GeoJSON.LineString, FlightPathFeatureProps> {
  const features: GeoJSON.Feature<GeoJSON.LineString, FlightPathFeatureProps>[] = [];
  for (const a of list) {
    if (a.groundSpeed == null || a.track == null || a.groundSpeed < 20) continue;
    const dtSec = extrapolationDtFor(a, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec);
    const start = extrapolate(a, dtSec);
    if (!start) continue;
    const seconds = a.icao === selectedIcao ? PATH_SECONDS * 1.5 : PATH_SECONDS;
    const end = project(start, a.track, a.groundSpeed * 0.514444 * seconds);
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[start.lon, start.lat], [end.lon, end.lat]] },
      properties: {
        icao: a.icao,
        cls: a.flightClass,
        selected: a.icao === selectedIcao,
        special: a.specialKind,
        emergency: isEmergency(a),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

/**
 * Build a GeoJSON FeatureCollection of aircraft-silhouette polygons (one per
 * airborne aircraft) used by the `fill-extrusion` altitude-pillar layer. Each
 * footprint is rotated to the aircraft's heading so the extruded "hologram"
 * matches the plane icon's orientation. `altM` is the exaggerated altitude in
 * meters; grounded aircraft are omitted.
 */
export function buildAircraftAltitudeGeoJSON(
  list: RichAircraft[],
  snapshotTimeSec: number,
  nowSec: number,
  selectedIcao: string | null,
  selectedFetchedAtSec = 0,
): GeoJSON.FeatureCollection<GeoJSON.Polygon, AircraftAltitudeFeatureProps> {
  const features: GeoJSON.Feature<GeoJSON.Polygon, AircraftAltitudeFeatureProps>[] = [];
  for (const a of list) {
    const dtSec = extrapolationDtFor(a, snapshotTimeSec, nowSec, selectedIcao, selectedFetchedAtSec);
    const pos = extrapolate(a, dtSec);
    if (!pos) continue;
    const altFeet = a.onGround ? 0 : a.altBaro ?? 0;
    if (altFeet <= 0) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          aircraftShapeRing(pos.lon, pos.lat, a.track, silhouetteFor(a.iconKind), a.iconKind),
        ],
      },
      properties: {
        icao: a.icao,
        altM: altFeet * FEET_TO_METERS * ALTITUDE_EXAGGERATION,
        emergency: isEmergency(a),
        selected: a.icao === selectedIcao,
        icon: a.iconKind,
        special: a.specialKind,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

function aircraftShapeRing(
  lon: number,
  lat: number,
  headingDeg: number | null,
  shape: readonly [number, number][],
  iconKind: string,
): GeoJSON.Position[] {
  const halfSpan = pillarHalfSpanM(iconKind);
  const mPerDegLat = 111320;
  const mPerDegLon = 111320 * Math.cos((lat * Math.PI) / 180);
  const t = ((headingDeg ?? 0) * Math.PI) / 180;
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  const ring = shape.map(([x, y]) => {
    const east = (x * cos + y * sin) * halfSpan;
    const north = (-x * sin + y * cos) * halfSpan;
    return [lon + east / mPerDegLon, lat + north / mPerDegLat] as GeoJSON.Position;
  });
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push(first);
  }
  return ring;
}

type IconDef = (typeof ICON_DEFS)[string];

/** Load the aircraft-type icon atlas into the map style. Returns true if all loaded. */
export async function loadPlaneIcons(
  map: { addImage: (id: string, img: HTMLImageElement | ImageData) => void; hasImage?: (id: string) => boolean },
): Promise<boolean> {
  const entries = Object.entries(ICON_DEFS);
  const results = await Promise.all(
    entries.flatMap(([kind, def]) => [
      loadIcon(map, `plane-${kind}`, def),
      loadIcon(map, `plane-${kind}-emergency`, { ...def, color: '#ef4444', preserveColor: false }),
    ]),
  );
  return results.every(Boolean);
}

async function loadIcon(
  map: { addImage: (id: string, img: HTMLImageElement | ImageData) => void; hasImage?: (id: string) => boolean },
  id: string,
  def: IconDef,
): Promise<boolean> {
  if (map.hasImage?.(id)) return true;
  const raw = await fetch(`${AIRCRAFT_ICON_BASE}${def.file}`).then((res) => (res.ok ? res.text() : ''));
  if (!raw) return false;
  const svg = normalizeSvgSize(def.preserveColor ? raw : recolorSvg(raw, def.color!));
  const img = await imageFromSvg(svg).catch(() => null);
  if (!img) return false;
  try {
    map.addImage(id, img);
    return true;
  } catch {
    return false;
  }
}

function imageFromSvg(svg: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const size = ICON_CANVAS_PX;
    const img = new Image(size, size);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('aircraft icon canvas unavailable'));
        return;
      }
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      resolve(ctx.getImageData(0, 0, size, size));
    };
    img.onerror = () => reject(new Error('aircraft icon failed to load'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}
